import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useFeatureFlags } from '@node-space/hooks'
import Box, { Box as CalloutContainer } from '@node-space/storybook-components/dist/Box'
import Callout from '@node-space/storybook-components/dist/Callout'
import PageHeader from '@node-space/storybook-components/dist/PageHeader'
import SmallTabs from '@node-space/storybook-components/dist/SmallTabs'
import Text from '@node-space/storybook-components/dist/Text'
import { PathNames } from 'constants/General'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useShowVerifiedBannerMutation } from 'hooks/mutations/useShowVerifiedBannerMutation'
import { useAccountPreferencesQuery } from 'hooks/queries'
import { useWalletsQuery } from 'hooks/queries/useWalletsQuery'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { WalletEditModal } from 'pages/Wallets/components/Modal/WalletEditModal'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { useGetTotalBalance } from '../../hooks/queries/useGetTotalBalance'
import { WalletType, WalletTypes } from '../../types/types'
import WalletHeaderButtons from '../Wallets/components/WalletHeaderButtons'
import UnsupportedWalletAction from './components/Modal/UnsupportedWalletAction'
import WalletCreateModal from './components/Modal/WalletCreateModal'
import WalletModal, { WalletAction } from './components/Modal/WalletModal'
import { SearchWalletTable, searchWalletTableTags } from './components/Table/SearchWalletTable'
import WalletTable from './components/Table/WalletTable'
import WalletTotalBalance from './components/Table/WalletTotalBalance'
import { WalletCreateModalProvider } from './context/WalletCreateModalProvider'
import { WalletControllerProvider } from './context/WalletProvider'
import type { TableSection } from './types'

type TabQuery = 'transaction-history' | 'wallets'

const tabsData = {
  WALLETS: 'wallets',
  TRANSACTION_HISTORY: 'transaction-history',
}

const Wallets = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const profile = useProfileContext()
  const navigate = useNavigate()
  const permissions = useMappedRoles()?.wallets

  const [closedVerifiedBanner, setClosedVerifiedBanner] = useState(false)

  const { mutate: putShowVerifiedBanner } = useShowVerifiedBannerMutation()

  const {
    profile: { currencyCode: baseCurrencyCode },
    viewOnly,
  } = profile

  const {
    data: accountPreferences,
    isLoading: isLoadingAccountPreferences,
    isFetching: isFetchingAccountPreferences,
    isError: isErrorAccountPreferences,
  } = useAccountPreferencesQuery()

  const baseCurrency = accountPreferences?.currency || baseCurrencyCode

  const showVerifiedBanner = useMemo(
    () => !isLoadingAccountPreferences && accountPreferences?.showVerifiedBanner,
    [isLoadingAccountPreferences, accountPreferences]
  )

  const {
    data: wallets = [],
    isLoading: isLoadingWallets,
    isFetching: isFetchingWallets,
    isError: isErrorWallets,
  } = useWalletsQuery()

  const {
    data: totalBalance,
    isLoading: isLoadingTotalBalance,
    isFetching: isFetchingTotalBalance,
  } = useGetTotalBalance(baseCurrency)

  const {
    enableAcl,
    enableSearchWalletTransactions,
    enableWalletDashboardRedirect,
    disabledCryptoWallets: disableCryptoWallets,
  } = useFeatureFlags()

  const [unsupportedWalletAction, setUnsupportedWalletAction] = useState<string>()
  const [showEditWalletModal, setShowEditWalletModal] = useState<boolean>(false)
  const [selectedWalletName, setSelectedWalletName] = useState<string>()
  const [selectedItem, setSelectedItem] = useState<string>()
  const [walletModalAction, setWalletModalAction] = useState<WalletAction>(undefined)

  const hasErrors = isErrorWallets || isErrorAccountPreferences
  const isLoading = isLoadingWallets || isLoadingTotalBalance || isLoadingAccountPreferences
  const isFetching = isFetchingWallets || isFetchingTotalBalance || isFetchingAccountPreferences
  const showSmallTabs = enableSearchWalletTransactions && !enableWalletDashboardRedirect

  const isEditDisabled = enableAcl && !permissions?.canEdit
  const canTrade = enableAcl ? permissions?.canTrade : true
  const urlParams = new URLSearchParams(window.location.search)
  const tab = urlParams.get('tab') as TabQuery

  const tabs = [
    {
      id: tabsData.WALLETS,
      tabHeader: t('wallets.title'),
      tabContent: null,
      onClick: () => {
        navigate(`${PathNames.WALLETS}${PathNames.WALLET_DASHBOARD}`, { replace: true })
      },
      testid: 'wallets-tab',
    },
    {
      id: tabsData.TRANSACTION_HISTORY,
      tabHeader: (
        <Box flex>
          {t('wallets.transactionHistory')}
          <Text
            size="xs"
            color="primary-500"
            className="ml-2 bg-purple-100 font-medium px-2.5 py-0.5 rounded"
          >
            {t('beta')}
          </Text>
        </Box>
      ),
      tabContent: (
        <SearchWalletTable pageId={searchWalletTableTags.WALLETS_TRANSACTION_HISTORY.id} />
      ),
      onClick: () => {
        track.Amp.track(AmplitudeEvent.WALLET_TRANSACTION_HISTORY_TAB_VIEWED, {
          category: AmplitudeEventCategory.WALLET_TRANSACTION_SEARCH,
          action: AmplitudeEventAction.NAVIGATE,
        })
        navigate(`${PathNames.WALLETS}${PathNames.WALLET_DASHBOARD}?tab=transaction-history`, {
          replace: true,
        })
      },
      testid: 'wallets-transaction-history-tab',
    },
  ]

  const handleEditWalletName = useCallback(
    (walletId: string, walletName: string) => {
      setSelectedItem(walletId)
      setSelectedWalletName(walletName)
      setShowEditWalletModal(true)
    },
    [setSelectedItem, setSelectedWalletName, setShowEditWalletModal]
  )

  const filteredWallets = useCallback(() => {
    const fiatWallets: WalletType[] = []
    const cryptoWallets: WalletType[] = []

    for (let index = 0; index < wallets?.length; index++) {
      const currentWallet = wallets?.[index]

      if (!wallets?.[index]?.currency?.fiat && disableCryptoWallets) {
        continue
      }

      currentWallet?.currency?.fiat
        ? fiatWallets.push(currentWallet)
        : cryptoWallets.push(currentWallet)
    }

    return {
      fiatWallets,
      cryptoWallets,
    }
  }, [wallets])

  const mappedWallets = filteredWallets()

  const sectionedWallets: TableSection[] = [
    {
      id: 'FIAT' as WalletTypes,
      title: t('fiat'),
      items: mappedWallets.fiatWallets,
    },
    {
      id: 'CRYPTO' as WalletTypes,
      title: t('cryptocurrency'),
      items: mappedWallets.cryptoWallets,
    },
  ]

  const refreshWallets = async () => {
    await queryClient.invalidateQueries(reactQueryKeys.wallets())
    await queryClient.invalidateQueries(reactQueryKeys.totalBalance(baseCurrency))
  }

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.WALLET_HOME, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  useEffect(() => {
    if (enableWalletDashboardRedirect && tab === tabsData.TRANSACTION_HISTORY) {
      navigate(PathNames.TRANSACTION_HISTORY)
    }
  }, [tab, enableWalletDashboardRedirect])

  const verifyWidgetCloseButton = () => {
    setClosedVerifiedBanner(true)
    putShowVerifiedBanner(false)
  }

  return (
    <WalletControllerProvider
      isLoading={isLoading}
      isFetching={isFetching}
      hasErrors={hasErrors}
      baseCurrency={baseCurrency}
      totalBalance={totalBalance?.total}
      totalFiat={totalBalance?.totalFiat}
      totalCrypto={totalBalance?.totalCrypto}
    >
      <WalletCreateModalProvider
        condition={location.pathname === `${PathNames.WALLETS}${PathNames.WALLET_CREATE}`}
      >
        {showVerifiedBanner && !closedVerifiedBanner && enableWalletDashboardRedirect && (
          <CalloutContainer paddingB={16}>
            <Callout
              onClose={verifyWidgetCloseButton}
              showCloseButton
              state="success"
              message={t('verification.successMessage')}
            />
          </CalloutContainer>
        )}

        <PageHeader
          description={<WalletTotalBalance />}
          renderRightSection={
            <WalletHeaderButtons
              setWalletModalAction={setWalletModalAction}
              canEdit={!isEditDisabled}
              canTrade={canTrade}
              viewOnly={viewOnly}
            />
          }
        />

        {!!showSmallTabs && (
          <SmallTabs
            tabs={tabs}
            currentValueId={tab === tabsData.TRANSACTION_HISTORY ? tab : tabsData.WALLETS}
          />
        )}

        {(tab !== tabsData.TRANSACTION_HISTORY || enableWalletDashboardRedirect) && (
          <Box paddingT={16} paddingB={16} gapX={16}>
            <WalletTable
              sections={sectionedWallets}
              setWalletModalAction={setWalletModalAction}
              handleEditWalletName={handleEditWalletName}
            />
          </Box>
        )}

        <WalletModal
          action={walletModalAction}
          setWalletModalAction={setWalletModalAction}
          refresh={refreshWallets}
        />
        {(!viewOnly || !isEditDisabled) && (
          <WalletCreateModal refresh={refreshWallets}></WalletCreateModal>
        )}
        {unsupportedWalletAction && (
          <UnsupportedWalletAction
            setUnsupportedWalletAction={setUnsupportedWalletAction}
            unsupportedWalletAction={unsupportedWalletAction}
          />
        )}
        {showEditWalletModal && (
          <WalletEditModal
            setShowEditWalletModal={setShowEditWalletModal}
            selectedWalletName={selectedWalletName}
            selectedItem={selectedItem}
            setSelectedItem={setSelectedItem}
          />
        )}
      </WalletCreateModalProvider>
    </WalletControllerProvider>
  )
}

export default memo(Wallets)
